import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
// import { HiUser, HiOfficeBuilding } from 'react-icons/hi';
import { Button } from '@/components/common';
import { 
  Customer, 
  CreateCustomerRequest, 
  UpdateCustomerRequest, 
  Gender,
  CustomerGroup 
} from '@/types/customer';

import { categoryApi } from '@/api/endpoints/categories';
import { Category } from '@/types/category';
import { 
  requiresCompanyName, 
  mapCustomerTypeToCategoryId 
} from '@/constants/categoryTypes';
import { userAPI } from '@/api/endpoints/users';
import { UserLookup } from '@/types/user';

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (data: CreateCustomerRequest | UpdateCustomerRequest, driverId?: string) => void;
  onCancel: () => void;
  onSaveAndClose?: (data: CreateCustomerRequest | UpdateCustomerRequest, driverId?: string) => void;
  loading?: boolean;
  currentTab?: 'basic' | 'detailed';
  hideTabNavigation?: boolean;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  onSubmit,
  onCancel,
  onSaveAndClose,
  loading = false,
  currentTab,
  hideTabNavigation = false,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'basic' | 'detailed'>('basic');

  // Use external currentTab when provided (for edit modal), otherwise use internal state
  const effectiveActiveTab = currentTab || activeTab;
  
  // Form data
  const [formData, setFormData] = useState<CreateCustomerRequest>({
    name: '',
    phone: '',
    email: '',
    categoryTypeId: '',
    categoryGroupId: '',
    additionalName: '',
    address: {
      city: '',
      district: '',
      details: '',
    },
    notes: '',
    status: 'active',
    debit: 0,
  });

  // Additional data
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [customerTypes, setCustomerTypes] = useState<Category[]>([]);
  const [drivers, setDrivers] = useState<UserLookup[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');

  // Form validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Refs for form fields to enable focus management
  const fieldRefs = useRef<Record<string, HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null>>({});

  // State for formatted debt display
  const [formattedDebit, setFormattedDebit] = useState<string>('');

  // Helper functions for number formatting
  const formatNumberWithDots = (value: number): string => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Debounced validation for better UX
  const [debouncedPhone, setDebouncedPhone] = useState(formData.phone);
  const [debouncedEmail, setDebouncedEmail] = useState(formData.email || '');
  const [validatingPhone, setValidatingPhone] = useState(false);
  const [validatingEmail, setValidatingEmail] = useState(false);

  // Validation functions
  const validatePhone = (phone: string): string => {
    if (!phone.trim()) {
      return t('validation.required');
    }
    if (!/^[0-9\-\+\s()]{8,20}$/.test(phone)) {
      return t('validation.phone');
    }
    return '';
  };

  const validateEmail = (email: string): string => {
    if (email && email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email)) {
      return t('validation.email');
    }
    return '';
  };

  // Real-time validation for phone and email
  const validateField = (field: string, value: string) => {
    let error = '';
    
    switch (field) {
      case 'phone':
        error = validatePhone(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      default:
        break;
    }

    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Debounced validation effect
  useEffect(() => {
    if (formData.phone !== debouncedPhone) {
      setValidatingPhone(true);
      const phoneTimer = setTimeout(() => {
        setDebouncedPhone(formData.phone);
        setValidatingPhone(false);
      }, 500);

      return () => clearTimeout(phoneTimer);
    }
  }, [formData.phone, debouncedPhone]);

  useEffect(() => {
    if (formData.email !== debouncedEmail) {
      setValidatingEmail(true);
      const emailTimer = setTimeout(() => {
        setDebouncedEmail(formData.email || '');
        setValidatingEmail(false);
      }, 500);

      return () => clearTimeout(emailTimer);
    }
  }, [formData.email || '', debouncedEmail]);

  // Validate debounced values
  useEffect(() => {
    if (touched.phone) {
      validateField('phone', debouncedPhone);
    }
  }, [debouncedPhone, touched.phone]);

  useEffect(() => {
    if (touched.email) {
      validateField('email', debouncedEmail);
    }
  }, [debouncedEmail, touched.email]);

  // Load customer data for editing
  useEffect(() => {
    if (customer) {
      // Convert birthday from RFC3339 format to YYYY-MM-DD for date input
      let birthdayForInput = customer.birthday;
      if (birthdayForInput && birthdayForInput.includes('T')) {
        birthdayForInput = birthdayForInput.split('T')[0];
      }

      // Get categoryTypeId from customer data - check if customer has categoryTypeId directly
      // or derive it from customerType field for backward compatibility
      let categoryTypeId = '';
      if ((customer as any).categoryTypeId) {
        // Customer data already has categoryTypeId (from API response)
        categoryTypeId = (customer as any).categoryTypeId;
      } else if (customer.customerType) {
        // Fallback: derive from customerType field
        categoryTypeId = mapCustomerTypeToCategoryId(customer.customerType);
      }

      setFormData({
        name: customer.name || '', // Use name field directly
        phone: customer.phone,
        email: customer.email,
        categoryTypeId: categoryTypeId,
        categoryGroupId: customer.customerGroupId || '',
        additionalName: customer.companyName || '', // Additional Name field displays company value
        companyName: customer.companyName || '', // Company Name field
        address: customer.address,
        notes: customer.notes,
        status: customer.status,
        debit: customer.totalDebt || customer.debit || 0,
        gender: customer.gender,
        birthday: birthdayForInput,
        facebookLink: customer.facebookLink,
        taxCode: customer.taxCode,
      });

      // Initialize formatted debt display
      const debitValue = customer.totalDebt || customer.debit || 0;
      setFormattedDebit(debitValue > 0 ? formatNumberWithDots(debitValue) : '');

      // Set selected driver if customer has one assigned
      setSelectedDriverId(customer.assignedDriverId || '');
    }
  }, [customer]);

  // Load additional data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [groupsData, typesData, driversData] = await Promise.all([
          // Fetch customer groups from category API (active only)
          categoryApi.getCategories(1, 100, { type: ['customer_group'], status: ['active'] }).then(response =>
            response.data.map(category => ({
              id: category.code,
              name: category.name,
              description: category.description || '',
              discountPercentage: 0,
              customerCount: 0,
              createdAt: category.createdAt,
              updatedAt: category.updatedAt,
            }))
          ),
          // Fetch customer types from category API (active only)
          categoryApi.getCategories(1, 100, { type: ['customer_type'], status: ['active'] }),
          userAPI.lookupUsers('delivery_assignees'),
        ]);
        setCustomerGroups(groupsData);
        setCustomerTypes(typesData.data);
        setDrivers(driversData);
      } catch (error) {
        console.error('Error loading form data:', error);
      }
    };

    loadData();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Define validation order to ensure proper focus management
    const validationOrder = ['name', 'phone', 'email', 'customerType', 'companyName', 'city', 'district', 'details'];

    if (!formData.name.trim()) {
      newErrors.name = t('validation.required');
    }

    // Phone validation
    const phoneError = validatePhone(formData.phone);
    if (phoneError) {
      newErrors.phone = phoneError;
    }

    // Email validation (only format, email itself is optional)
    const emailError = validateEmail(formData.email || '');
    if (emailError) {
      newErrors.email = emailError;
    }

    if (!formData.categoryTypeId) {
      newErrors.categoryTypeId = t('validation.required');
    }

    // Check if selected category type requires company name
    if (requiresCompanyName(formData.categoryTypeId) && !formData.companyName?.trim()) {
      newErrors.companyName = t('validation.required');
    }

    // City, district and address details are optional
    // if (!formData.address.city) {
    //   newErrors.city = t('validation.required');
    // }

    // if (!formData.address.district) {
    //   newErrors.district = t('validation.required');
    // }

    setErrors(newErrors);

    // Focus management: focus on the first field with an error
    if (Object.keys(newErrors).length > 0) {
      // Find the first field with an error based on validation order
      const firstErrorField = validationOrder.find(field => newErrors[field]);

      if (firstErrorField && fieldRefs.current[firstErrorField]) {
        // Check if the error field is in the basic tab (most validation errors are there)
        const basicTabFields = ['name', 'phone', 'email', 'customerType', 'companyName', 'province', 'city', 'details'];
        const isBasicTabError = basicTabFields.includes(firstErrorField);

        // Switch to basic tab if the error is there and we're not already on it
        if (isBasicTabError && activeTab !== 'basic') {
          setActiveTab('basic');
        }

        // Small delay to ensure the error state is rendered and tab switch is complete
        setTimeout(() => {
          fieldRefs.current[firstErrorField]?.focus();
          fieldRefs.current[firstErrorField]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, isBasicTabError && activeTab !== 'basic' ? 200 : 100);
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Convert birth date to RFC3339 format if provided
    const processedFormData = { ...formData };
    if (processedFormData.birthday && processedFormData.birthday !== '') {
      processedFormData.birthday = `${processedFormData.birthday}T00:00:00Z`;
    }

    // Prepare API payload with correct field mappings
    const submitData = customer
      ? {
          ...processedFormData,
          id: customer.id,
          name: processedFormData.name, // Map Customer Name input to name field
          company: processedFormData.companyName, // Map Company Name input to company field
        } as UpdateCustomerRequest
      : {
          ...processedFormData,
          name: processedFormData.name, // Map Customer Name input to name field
          company: processedFormData.companyName, // Map Company Name input to company field
        };

    onSubmit(submitData, selectedDriverId || undefined);
  };

  const handleSaveAndClose = () => {
    if (!validateForm()) return;

    // Convert birth date to RFC3339 format if provided
    const processedFormData = { ...formData };
    if (processedFormData.birthday && processedFormData.birthday !== '') {
      processedFormData.birthday = `${processedFormData.birthday}T00:00:00Z`;
    }

    // Prepare API payload with correct field mappings
    const submitData = customer
      ? {
          ...processedFormData,
          id: customer.id,
          name: processedFormData.name, // Map Customer Name input to name field
          company: processedFormData.companyName, // Map Company Name input to company field
        } as UpdateCustomerRequest
      : {
          ...processedFormData,
          name: processedFormData.name, // Map Customer Name input to name field
          company: processedFormData.companyName, // Map Company Name input to company field
        };

    if (onSaveAndClose) {
      onSaveAndClose(submitData, selectedDriverId || undefined);
    }
  };

  const handleInputChange = (field: keyof CreateCustomerRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing (except for phone and email which use debounced validation)
    if (field !== 'phone' && field !== 'email' && errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddressChange = (field: keyof CreateCustomerRequest['address'], value: string) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Reset district when city changes
    if (field === 'city') {
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, district: '' }
      }));
    }
  };

  const handleFieldBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  return (
    <div className="bg-white min-h-[600px]">
      {/* Tab Navigation - only show when not hidden */}
      {!hideTabNavigation && (
        <div className="mobile-tab-scroll">
          <nav className="mobile-tab-list" aria-label="Customer Form Tabs">
            <button
              type="button"
              onClick={() => setActiveTab('basic')}
              className={`mobile-tab-button border-b-2 ${
                effectiveActiveTab === 'basic'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('customers.tabs.info')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('detailed')}
              className={`mobile-tab-button border-b-2 ${
                effectiveActiveTab === 'detailed'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('customers.tabs.detailedInfo')}
            </button>
          </nav>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-6 space-y-6 min-h-[500px]">
        {/* Validation Error Summary */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {Object.keys(errors).length === 1
                    ? t('validation.errorFound', 'Please fix the validation error below')
                    : t('validation.errorsFound', `Please fix the ${Object.keys(errors).length} validation errors below`)
                  }
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{t('validation.pleaseFixErrors', 'Please correct the highlighted fields and try again.')}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Basic Information Tab */}
        {effectiveActiveTab === 'basic' && (
          <div className="space-y-6 min-h-[400px]">
            {/* Customer Code (read-only for existing customer) */}
            {customer && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.code')}
                </label>
                <input
                  type="text"
                  value={customer.code}
                  disabled
                  className="mt-1 block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900"
                />
              </div>
            )}

            {/* Customer Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.fields.name')} <span className="text-red-500">*</span>
              </label>
              <input
                ref={(el) => fieldRefs.current.name = el}
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                onBlur={() => handleFieldBlur('name')}
                className={`mt-1 block w-full px-3 py-2 border rounded-md text-sm ${
                  errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                } focus:outline-none focus:ring-1`}
                placeholder={t('customers.fields.name')}
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            {/* Phone and Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.phone')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    ref={(el) => fieldRefs.current.phone = el}
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    onBlur={() => handleFieldBlur('phone')}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md text-sm pr-8 ${
                      errors.phone ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    } focus:outline-none focus:ring-1`}
                    placeholder="0901234567"
                  />
                  {validatingPhone && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    </div>
                  )}
                  {!validatingPhone && touched.phone && !errors.phone && formData.phone && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  {!validatingPhone && touched.phone && errors.phone && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                {!errors.phone && touched.phone && formData.phone && (
                  <p className="mt-1 text-sm text-green-600">{t('validation.phoneValid')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.email')}
                </label>
                <div className="relative">
                  <input
                    ref={(el) => fieldRefs.current.email = el}
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onBlur={() => handleFieldBlur('email')}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md text-sm pr-8 ${
                      errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    } focus:outline-none focus:ring-1`}
                    placeholder="example@email.com"
                  />
                  {validatingEmail && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    </div>
                  )}
                  {!validatingEmail && touched.email && !errors.email && formData.email && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  {!validatingEmail && touched.email && errors.email && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                {!errors.email && touched.email && formData.email && (
                  <p className="mt-1 text-sm text-green-600">{t('validation.emailValid')}</p>
                )}
              </div>
            </div>

            {/* Debt Fields */}
            <div className="grid grid-cols-1 gap-6">
              {/* Current Debt (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.currentDebt')}
                </label>
                <input
                  type="text"
                  value={formattedDebit}
                  readOnly
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 cursor-not-allowed"
                />
                <p className="mt-1 text-sm text-gray-500">
                  {t('customers.fields.currentDebtHelp')}
                </p>
              </div>
            </div>

            {/* Customer Type and Additional Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.customerType')} <span className="text-red-500">*</span>
                </label>
                <select
                  ref={(el) => fieldRefs.current.categoryTypeId = el}
                  value={formData.categoryTypeId}
                  onChange={(e) => handleInputChange('categoryTypeId', e.target.value)}
                  onBlur={() => handleFieldBlur('categoryTypeId')}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md text-sm ${
                    errors.categoryTypeId ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  } focus:outline-none focus:ring-1`}
                >
                  <option value="">{t('customers.fields.customerType')}</option>
                  {customerTypes.filter(type => type.status === 'active').map((type) => (
                    <option key={type.code} value={type.code}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {errors.categoryTypeId && <p className="mt-1 text-sm text-red-600">{errors.categoryTypeId}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.additionalName')}
                </label>
                <input
                  type="text"
                  value={formData.additionalName || ''}
                  onChange={(e) => handleInputChange('additionalName', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('customers.fields.additionalNamePlaceholder')}
                />
              </div>
            </div>

            {/* Company Name (conditional) */}
            {requiresCompanyName(formData.categoryTypeId) && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.companyName')} <span className="text-red-500">*</span>
                </label>
                <input
                  ref={(el) => fieldRefs.current.companyName = el}
                  type="text"
                  value={formData.companyName || ''}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  onBlur={() => handleFieldBlur('companyName')}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md text-sm ${
                    errors.companyName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  } focus:outline-none focus:ring-1`}
                  placeholder={t('customers.fields.companyName')}
                />
                {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>}
              </div>
            )}

            {/* Address */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">{t('customers.fields.address')}</h3>
              
              {/* City and District */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('customers.fields.city')}
                  </label>
                  <input
                    type="text"
                    ref={(el) => fieldRefs.current.city = el}
                    value={formData.address.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    onBlur={() => handleFieldBlur('city')}
                    placeholder={t('customers.fields.city')}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md text-sm ${
                      errors.city ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    } focus:outline-none focus:ring-1`}
                  />
                  {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('customers.fields.district')}
                  </label>
                  <input
                    type="text"
                    ref={(el) => fieldRefs.current.district = el}
                    value={formData.address.district}
                    onChange={(e) => handleAddressChange('district', e.target.value)}
                    onBlur={() => handleFieldBlur('district')}
                    placeholder={t('customers.fields.district')}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md text-sm ${
                      errors.district ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    } focus:outline-none focus:ring-1`}
                  />
                  {errors.district && <p className="mt-1 text-sm text-red-600">{errors.district}</p>}
                </div>
              </div>

              {/* Address Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.details')}
                </label>
                <input
                  ref={(el) => fieldRefs.current.details = el}
                  type="text"
                  value={formData.address.details}
                  onChange={(e) => handleAddressChange('details', e.target.value)}
                  onBlur={() => handleFieldBlur('details')}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md text-sm ${
                    errors.details ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  } focus:outline-none focus:ring-1`}
                  placeholder="123 Nguyễn Huệ, Phường Bến Nghé"
                />
                {errors.details && <p className="mt-1 text-sm text-red-600">{errors.details}</p>}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.fields.notes')}
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                onBlur={() => handleFieldBlur('notes')}
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('customers.fields.notes')}
              />
            </div>
          </div>
        )}

        {/* Detailed Information Tab */}
        {effectiveActiveTab === 'detailed' && (
          <div className="space-y-6 min-h-[400px]">
            {/* Gender and Birthday */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.gender')}
                </label>
                <select
                  value={formData.gender || ''}
                  onChange={(e) => handleInputChange('gender', e.target.value as Gender)}
                  onBlur={() => handleFieldBlur('gender')}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('customers.fields.gender')}</option>
                  <option value="male">{t('customers.gender.male')}</option>
                  <option value="female">{t('customers.gender.female')}</option>
                  <option value="other">{t('customers.gender.other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.birthday')}
                </label>
                <input
                  type="date"
                  value={formData.birthday || ''}
                  onChange={(e) => handleInputChange('birthday', e.target.value)}
                  onBlur={() => handleFieldBlur('birthday')}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Facebook and Tax Code */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.facebookLink')}
                </label>
                <input
                  type="url"
                  value={formData.facebookLink || ''}
                  onChange={(e) => handleInputChange('facebookLink', e.target.value)}
                  onBlur={() => handleFieldBlur('facebookLink')}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://facebook.com/username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('customers.fields.taxCode')}
                </label>
                <input
                  type="text"
                  value={formData.taxCode || ''}
                  onChange={(e) => handleInputChange('taxCode', e.target.value)}
                  onBlur={() => handleFieldBlur('taxCode')}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0123456789"
                />
              </div>
            </div>

            {/* Customer Group */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.fields.customerGroup')}
              </label>
              <select
                value={formData.categoryGroupId || ''}
                onChange={(e) => handleInputChange('categoryGroupId', e.target.value)}
                onBlur={() => handleFieldBlur('categoryGroupId')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('customers.fields.customerGroup')}</option>
                {customerGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Assigned Driver */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Tài xế phụ trách
              </label>
              <select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Chọn tài xế --</option>
                {drivers.map((driver) => (
                  <option key={driver.actor_id} value={driver.actor_id}>
                    {driver.full_name} ({driver.role})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Tài xế này sẽ tự động được phân công giao hàng cho các đơn hàng của khách hàng
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            {t('buttons.cancel')}
          </Button>
          
          {onSaveAndClose && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleSaveAndClose}
              loading={loading}
            >
              {t('buttons.saveAndClose')}
            </Button>
          )}
          
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            {customer ? t('buttons.save') : t('buttons.create')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomerForm;
